import React, { useEffect, useRef, useState } from "react"
import { Carousel, Typography, Button, Modal } from "antd"
import { LeftOutlined, RightOutlined, CloseOutlined } from "@ant-design/icons"
import { css } from "@emotion/react"
import IMAGE_DEFAULT from '@/assets/images/image_default.png';

const { Title, Text } = Typography

interface ProductInfoSectionProps {
  isExpanded?: boolean
  onToggle?: () => void
  products?: Product[]
  initialIndex?: number
}

const ProductInfoSection: React.FC<ProductInfoSectionProps> = ({ isExpanded = true, onToggle, products = [], initialIndex = 0 }) => {
  const carouselRef = useRef<any>(null)
  const [activeIndex, setActiveIndex] = useState<number>(initialIndex)

  const displayProducts = products.map((product: Product) => ({
    id: product.idFood,
    image: product.thumbnail,
    name: product.name,
    shortDesc: product.shortDesc
  }))

  const handlePrev = () => {
    carouselRef.current?.prev()
  }

  const handleNext = () => {
    carouselRef.current?.next()
  }

  useEffect(() => {
    setActiveIndex(initialIndex)
    if (carouselRef.current && typeof carouselRef.current.goTo === 'function') {
      carouselRef.current.goTo(initialIndex, true)
    }
  }, [initialIndex])

  return (
    <Modal
      open={isExpanded}
      onCancel={onToggle}
      footer={null}
      centered
      css={modalStyles}
      closeIcon={<CloseOutlined />}
    >
      <div>
        <div css={headerStyles}>
          <Title level={5} style={{ color: '#333333'}}>
            Thông tin thêm
          </Title>
        </div>

        <div css={contentStyles}>
          <Text css={descriptionStyles}>
            Món ăn tư vấn cho bạn có hương vị thơm ngon khi sử dụng các gia vị dưới đây. Bạn hãy cùng khám phá nhé!
          </Text>

          {displayProducts.length > 0 && (
            <div css={showcaseStyles}>
              <Title level={5} css={productTitleStyles}>
                {displayProducts[activeIndex]?.name}
              </Title>

              <div css={carouselContainerStyles}>
                <Button
                  type="text"
                  icon={<LeftOutlined />}
                  css={arrowButtonStyles}
                  onClick={handlePrev}
                />

                <div css={carouselWrapperStyles}>
                  <Carousel
                    ref={carouselRef}
                    dots={false}
                    slidesToShow={Math.min(5, displayProducts.length)}
                    slidesToScroll={1}
                    infinite={displayProducts.length > 1}
                    initialSlide={initialIndex}
                    afterChange={(current) => {
                      setActiveIndex(current)
                    }}
                    centerMode
                    centerPadding="0px"
                  >
                    {displayProducts.map((product, idx) => (
                      <div key={`${product.id}-${idx}`} css={productSlideStyles}>
                        <div css={productItemStyles}>
                          <img
                            src={product.image || IMAGE_DEFAULT}
                            alt={product.name}
                            css={productImageStyles}
                          />
                        </div>
                      </div>
                    ))}
                  </Carousel>
                </div>

                <Button
                  type="text"
                  icon={<RightOutlined />}
                  css={arrowButtonStyles}
                  onClick={handleNext}
                />
              </div>

              <Text css={productDescriptionStyles}>
                {displayProducts[activeIndex]?.shortDesc}
              </Text>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default ProductInfoSection

const modalStyles = css`
  .ant-modal-content {
    border: 1px solid #D6D6D6;
    padding: 0;
  }
`

const headerStyles = css`
  padding: 1.2rem;
`

const contentStyles = css`
  padding: 1.2rem;
  padding-top: 0;
`

const descriptionStyles = css`
  display: block;
  font-size: 1.4rem;
  color: #000000;
  line-height: 1.4;
  margin-bottom: 1.6rem;
  text-align: center;
  padding: 0 0.2rem;
`

const showcaseStyles = css`
  border: 1px solid #D71C1C;
  padding: 0.4rem 1.2rem;
`

const productTitleStyles = css`
  color: #FF7D01 !important;
  text-align: center;
  font-size: 1.4rem;
`

const carouselContainerStyles = css`
  position: relative;
  height: 13.2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 auto;
`

const carouselWrapperStyles = css`
  height: 13.2rem;
  overflow: hidden;
  width: 100%;
  
  .ant-carousel {
    height: 100%;
  }
  
  .ant-carousel .slick-slider {
    height: 100%;
  }
  
  .ant-carousel .slick-list {
    height: 100%;
  }
  
  .ant-carousel .slick-track {
    height: 100%;
  }
`

const productSlideStyles = css`
  padding: 0 0.4rem;
  height: 13.2rem;
`

const productItemStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 0.8rem;
`

const productImageStyles = css`
  width: 8rem;
  object-fit: contain;
  border-radius: 0.4rem;
  aspect-ratio: 1/2 ;
`

const arrowButtonStyles = css`
  width: 3.2rem;
  border-radius: 50%;
  border: 1px solid #d9d9d9;
  
  &:hover {
    border-color: #fa8c16;
    color: #fa8c16;
    background: #fff7e6;
  }
`

const productDescriptionStyles = css`
  color: #333333;
  line-height: 1.8;
  margin-top: 1.2rem;
  text-align: center;
  display: block;
  max-height: 10rem;
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.25) transparent;

  &::-webkit-scrollbar {
    width: 0.6rem;
    height: 0.6rem;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.25);
    border-radius: 0.4rem;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
`
